// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
interface IMaterialOverrideLayer {
    source: string;            // "selection", confirmed
    priority: number;          // lower wins
    materials: string[];       // per master mesh (or single entry applied to all)
}

const SELECTION_LAYER_SOURCE = "selection";
const HIDDEN_MATERIAL_NAME = "HidMat";

class MaterialOverrideManager implements Interfaces.Interfaces.IMaterialOverrideManager {

    private _meshManager: Interfaces.Interfaces.IMeshManager;

    // assetId -> base material names (one per master mesh)
    private _assetBaseMaterials: { [assetId: string]: string[] } = {};
    private _assetBaseMaterialsByMaster: { [assetId: string]: { [masterMeshId: string]: string } } = {};

    // assetId -> source -> layer
    private _assetLayers: { [assetId: string]: { [source: string]: IMaterialOverrideLayer } } = {};

    // GLOBAL: source -> layer (applies to every asset that has base info)
    private _globalLayers: { [source: string]: IMaterialOverrideLayer } = {};

    /**
     * assetId -> masterMeshId -> currently applied "signature"
     * null = base (no override winner), otherwise includes BOTH source + resolved material name.
     */
    private _assetCurrentSignature: { [assetId: string]: { [masterMeshId: string]: string | null } } = {};
    private _batchDepth: number = 0;
    private _isBatchFlushing: boolean = false;
    /** Dirty assets to resolve once at endBatch() */
    private _dirtyAssets: { [assetId: string]: boolean } = {};

    private _pendingMeshMatChanges: { [masterMeshId: string]: { [matName: string]: string[] } } = {};

    constructor(meshManager: Interfaces.Interfaces.IMeshManager) {
        this._meshManager = meshManager;
    }

    public beginBatch(): void {
        this._batchDepth++;
    }

    public endBatch(): void {
        if (this._batchDepth <= 0) return;

        this._batchDepth--;
        if (this._batchDepth > 0) return;

        const dirtyIds = Object.keys(this._dirtyAssets);
        this._dirtyAssets = {};

        // Keep enqueuing active during flush
        this._isBatchFlushing = true;
        try {
            for (let i = 0; i < dirtyIds.length; i++) {
                this._reapplyForAsset(dirtyIds[i]); // will enqueue instead of calling mm directly
            }
        } finally {
            this._isBatchFlushing = false;
        }

        this._flushPendingMeshMatChanges();
    }

    public clearSceneState(): void {
        this._assetBaseMaterials = {};
        this._assetBaseMaterialsByMaster = {};
        this._assetCurrentSignature = {};
        this._dirtyAssets = {};
        this._pendingMeshMatChanges = {};
        this._batchDepth = 0;
        this._isBatchFlushing = false;
    }

    private _markDirty(assetId: string): void {
        if (!assetId) return;

        if (this._batchDepth > 0 || this._isBatchFlushing) {
            this._dirtyAssets[assetId] = true;
        } else {
            this._reapplyForAsset(assetId);
        }
    }

    private _invalidateAssetSignatures(assetId: string): void {
        if (!assetId) return;

        delete this._assetCurrentSignature[assetId];
    }

    private _enqueueMeshMaterialChange(masterMeshId: string, matName: string, assetId: string): void {
        if (!masterMeshId || !matName || !assetId) return;

        if (!this._pendingMeshMatChanges[masterMeshId]) {
            this._pendingMeshMatChanges[masterMeshId] = {};
        }

        const byMat = this._pendingMeshMatChanges[masterMeshId];

        if (!byMat[matName]) {
            byMat[matName] = [];
        }

        byMat[matName].push(assetId);
    }

    private _flushPendingMeshMatChanges(): void {
        const mm = this._meshManager;

        for (const masterMeshId in this._pendingMeshMatChanges) {
            const byMat = this._pendingMeshMatChanges[masterMeshId];
            if (!byMat) continue;

            for (const matName in byMat) {
                const assetIds = byMat[matName];
                if (!assetIds || assetIds.length === 0) continue;

                mm.changeGraphicObjectsMaterial(masterMeshId, assetIds, matName, false);
            }
        }

        this._pendingMeshMatChanges = {};
    }

    // ---------------------------------------------------------------------
    //  Base info
    // ---------------------------------------------------------------------
    public ensureBaseInfo(assetId: string): boolean {

        // If we already have base info, we're good.
        if (this._assetBaseMaterials[assetId]) {
            const existingMasterMeshIds = this._meshManager.getMasterKeysOfObject(assetId) || [];
            if (existingMasterMeshIds.length > 0 && !this._assetBaseMaterialsByMaster[assetId]) {
                this._storeBaseMaterialsForMasters(assetId, existingMasterMeshIds, this._assetBaseMaterials[assetId], false);
            }
            return true;
        }

        const masterMeshIds = this._meshManager.getMasterKeysOfObject(assetId);
        const baseMaterials = this._meshManager.getOverrideMaterialOfObject(assetId);

        if (!masterMeshIds || !baseMaterials ||
            masterMeshIds.length === 0 || baseMaterials.length === 0) {
            return false; // geometry not ready yet
        }

        this._storeBaseMaterialsForMasters(assetId, masterMeshIds, baseMaterials, false);
        return true;
    }

    public getBaseMaterials(assetId: string): string[] | null {
        const masterMeshIds = this._meshManager.getMasterKeysOfObject(assetId) || [];
        const baseMaterials = this._getStoredBaseMaterials(assetId, masterMeshIds);
        return baseMaterials && baseMaterials.length > 0 ? baseMaterials : null;
    }

    public getMasterMeshIds(assetId: string): string[] | null {
        const masterMeshIds = this._meshManager.getMasterKeysOfObject(assetId);
        return (masterMeshIds && masterMeshIds.length > 0) ? masterMeshIds : null;
    }

    public maintainAsset(assetId: string, baseMaterialIds: string[]): void {
        if (!assetId || !baseMaterialIds || baseMaterialIds.length === 0) {
            return;
        }

        const masterMeshIds = this._meshManager.getMasterKeysOfObject(assetId);
        if (!masterMeshIds || masterMeshIds.length === 0) {
            return;
        }

        // Most maintainAsset callers can still observe the currently applied override material,
        // so preserve existing base info by default. The cylinder payload path is the exception:
        // it provides a single authoritative base material that should refresh cylinder masters.
        const shouldRefreshCylinderBaseMaterials = baseMaterialIds.length === 1
            && masterMeshIds.some(masterMeshId => masterMeshId && masterMeshId.indexOf("CylinderInstance|") === 0);

        this._storeBaseMaterialsForMasters(
            assetId,
            masterMeshIds,
            baseMaterialIds,
            !shouldRefreshCylinderBaseMaterials
        );

        const storedBaseMaterials = this._getStoredBaseMaterials(assetId, masterMeshIds);
        if (!storedBaseMaterials || storedBaseMaterials.length === 0) {
            return;
        }

        for (let i = 0; i < masterMeshIds.length; i++) {
            const matName = storedBaseMaterials.length > i ? storedBaseMaterials[i] : storedBaseMaterials[0];
            if (!matName) {
                continue;
            }

            if (!this._assetCurrentSignature[assetId]) {
                this._assetCurrentSignature[assetId] = {};
            }

            this._assetCurrentSignature[assetId][masterMeshIds[i]] = this._makeSignature(null, matName);
        }

        // Apply winner (global/per-asset) if any
        this._markDirty(assetId);
    }

    public reapplyAssets(assetIds: string[]): void {
        if (!assetIds || assetIds.length === 0) return;

        this.beginBatch();
        try {
            for (let i = 0; i < assetIds.length; i++) {
                const assetId = assetIds[i];
                if (!assetId) continue;
                this._invalidateAssetSignatures(assetId);
                this._markDirty(assetId);
            }
        } finally {
            this.endBatch();
        }
    }

    // ---------------------------------------------------------------------
    //  Layers (per-asset)
    // ---------------------------------------------------------------------

    public setOverrideForSource(assetId: string, source: string, priority: number, materials: string[]): void {
        if (!assetId || !source) return;

        if (!this._assetLayers[assetId]) {
            this._assetLayers[assetId] = {};
        }

        const normalizedMaterials = (materials || []).slice(0);
        const existing = this._assetLayers[assetId][source];
        if (existing &&
            existing.priority === priority &&
            existing.materials.length === normalizedMaterials.length &&
            existing.materials.every((material, index) => material === normalizedMaterials[index])) {
            return;
        }

        this._assetLayers[assetId][source] = {
            source,
            priority,
            materials: normalizedMaterials
        };

        this._invalidateAssetSignatures(assetId);

        // Only attempt to apply if base info exists
        if (this._assetBaseMaterials[assetId]) {
            this._markDirty(assetId);
        }
    }

    public clearOverrideForSource(assetId: string, source: string): void {
        if (!assetId || !source) return;

        const layers = this._assetLayers[assetId];
        if (!layers || !layers[source]) {
            return;
        }

        delete layers[source];

        let empty = true;
        for (const k in layers) { empty = false; break; }
        if (empty) {
            delete this._assetLayers[assetId];
        }

        this._invalidateAssetSignatures(assetId);
        this._markDirty(assetId);
    }

    public clearAllOverridesForAsset(assetId: string): void {
        if (!assetId) return;

        delete this._assetLayers[assetId];
        this._invalidateAssetSignatures(assetId);
        this._markDirty(assetId);
    }

    // ---------------------------------------------------------------------
    //  Layers (GLOBAL)
    // ---------------------------------------------------------------------
    public setGlobalOverrideForSource(source: string, priority: number, materials: string[]): void {
        if (!source) return;

        this._globalLayers[source] = {
            source,
            priority,
            materials: (materials || []).slice(0)
        };

        // Global changes affect all maintained assets.
        // If batching, mark all as dirty and resolve once at endBatch()
        if (this._batchDepth > 0) {
            for (const assetId in this._assetBaseMaterials) {
                this._dirtyAssets[assetId] = true;
            }
        } else {
            this.beginBatch();
            try {
                for (const assetId in this._assetBaseMaterials) {
                    this._dirtyAssets[assetId] = true;
                }
            } finally {
                this.endBatch();
            }
        }
    }

    public clearGlobalOverrideForSource(source: string): void {
        if (!source) return;

        if (!this._globalLayers[source]) return;
        delete this._globalLayers[source];

        if (this._batchDepth > 0) {
            for (const assetId in this._assetBaseMaterials) {
                this._dirtyAssets[assetId] = true;
            }
        } else {
            this.beginBatch();
            try {
                for (const assetId in this._assetBaseMaterials) {
                    this._dirtyAssets[assetId] = true;
                }
            } finally {
                this.endBatch();
            }
        }
    }

    // ---------------------------------------------------------------------
    //  Forget / seed
    // ---------------------------------------------------------------------

    /**
     * Forget "in-scene" info only IF the asset is no longer present in any master mesh.
     * Layers are remembered regardless.
     */
    public forgetAsset(assetId: string): void {
        if (!assetId) return;

        const masterMeshIds = this._meshManager.getMasterKeysOfObject(assetId);
        const stillInScene = !!(masterMeshIds && masterMeshIds.length > 0);

        if (!stillInScene) {
            this._hardForget(assetId);
        }
    }

    /** Batch version of forgetAsset() */
    public forgetAssets(assetIds: any[][]): void {
        if (!assetIds || assetIds.length === 0) return;

        for (let x = 0; x < assetIds.length; x++) {
            const ids = assetIds[x];
            for (let y = 0; y < ids.length; y++) {
                const id = ids[y];
                const assetId = (id && (id.objectId || id)) as any;
                if (!assetId) continue;

                this.forgetAsset(assetId);
            }
        }
    }

    /**
     * Seed only base material info. Master meshes are always retrieved from MeshManager.
     */
    public seedBaseInfo(assetId: string, baseMaterials: string[]): void {
        if (!assetId) return;
        if (!baseMaterials || baseMaterials.length === 0) {
            return;
        }

        const masterMeshIds = this._meshManager.getMasterKeysOfObject(assetId) || [];
        this._storeBaseMaterialsForMasters(assetId, masterMeshIds, baseMaterials, false);

        if (!this._assetCurrentSignature[assetId]) this._assetCurrentSignature[assetId] = {};

        const storedBaseMaterials = this._getStoredBaseMaterials(assetId, masterMeshIds);
        if (storedBaseMaterials) {
            for (let i = 0; i < masterMeshIds.length; i++) {
                const matName = storedBaseMaterials.length > i ? storedBaseMaterials[i] : storedBaseMaterials[0];
                if (!matName) {
                    continue;
                }

                this._assetCurrentSignature[assetId][masterMeshIds[i]] = this._makeSignature(null, matName);
            }
        }

        this._markDirty(assetId);
    }

    // ---------------------------------------------------------------------
    //  Internal: choose winner and apply
    // ---------------------------------------------------------------------

    private _reapplyForAllMaintainedAssets(): void {
        // "Maintained" == we have base materials for it.
        for (const assetId in this._assetBaseMaterials) {
            this._reapplyForAsset(assetId);
        }

        // If we are batching, the above calls will enqueue but not flush
        if (this._batchDepth > 0) {
            this._flushPendingMeshMatChanges();
        }
    }

    /** Resolve which material to use for a given mesh index. */
    private _resolveMaterialForIndex(targetMaterials: string[], baseMaterials: string[], index: number): string | null {
        if (!baseMaterials || baseMaterials.length === 0) return null;

        // Prefer per-mesh material if provided, otherwise fall back to [0], otherwise base.
        const fromTarget =
            (targetMaterials && targetMaterials.length > index && targetMaterials[index]) ? targetMaterials[index] :
                (targetMaterials && targetMaterials.length > 0 && targetMaterials[0]) ? targetMaterials[0] :
                    null;

        return fromTarget ?? (baseMaterials.length > index ? baseMaterials[index] : baseMaterials[0] ?? null);
    }

    private _makeSignature(source: string | null, matName: string): string {
        return `${source ?? 'BASE'}||${matName}`;
    }

    private _storeBaseMaterialsForMasters(assetId: string, masterMeshIds: string[], baseMaterials: string[], preserveExisting: boolean): void {
        if (!assetId || !masterMeshIds || masterMeshIds.length === 0 || !baseMaterials || baseMaterials.length === 0) {
            return;
        }

        if (!this._assetBaseMaterialsByMaster[assetId]) {
            this._assetBaseMaterialsByMaster[assetId] = {};
        }

        const byMaster = this._assetBaseMaterialsByMaster[assetId];

        if (baseMaterials.length === masterMeshIds.length) {
            for (let i = 0; i < masterMeshIds.length; i++) {
                const masterMeshId = masterMeshIds[i];
                if (!masterMeshId || (preserveExisting && byMaster[masterMeshId] != null)) {
                    continue;
                }

                const matName = baseMaterials[i] || baseMaterials[0];
                if (matName) {
                    byMaster[masterMeshId] = matName;
                }
            }
        }
        else if (baseMaterials.length === 1) {
            let targetMasterMeshIds = masterMeshIds;
            const cylinderMasterMeshIds = masterMeshIds.filter(masterMeshId => masterMeshId && masterMeshId.indexOf("CylinderInstance|") === 0);

            if (cylinderMasterMeshIds.length > 0) {
                targetMasterMeshIds = cylinderMasterMeshIds;
            }

            for (let i = 0; i < targetMasterMeshIds.length; i++) {
                const masterMeshId = targetMasterMeshIds[i];
                if (!masterMeshId || (preserveExisting && byMaster[masterMeshId] != null)) {
                    continue;
                }

                byMaster[masterMeshId] = baseMaterials[0];
            }
        }

        const orderedBaseMaterials = this._getStoredBaseMaterials(assetId, masterMeshIds);
        if (orderedBaseMaterials && orderedBaseMaterials.length > 0) {
            this._assetBaseMaterials[assetId] = orderedBaseMaterials;
        }
    }

    private _getStoredBaseMaterials(assetId: string, masterMeshIds: string[]): string[] | null {
        const byMaster = this._assetBaseMaterialsByMaster[assetId] || {};
        const legacyBaseMaterials = this._assetBaseMaterials[assetId] || [];
        const orderedBaseMaterials: string[] = [];

        if (!masterMeshIds || masterMeshIds.length === 0) {
            return legacyBaseMaterials.length > 0 ? legacyBaseMaterials.slice(0) : null;
        }

        for (let i = 0; i < masterMeshIds.length; i++) {
            const masterMeshId = masterMeshIds[i];
            const matName = byMaster[masterMeshId]
                || (legacyBaseMaterials.length > i ? legacyBaseMaterials[i] : null)
                || (legacyBaseMaterials.length === 1 ? legacyBaseMaterials[0] : null);

            if (!matName) {
                return null;
            }

            orderedBaseMaterials.push(matName);
        }

        return orderedBaseMaterials;
    }

    private _hardForget(assetId: string): void {
        delete this._assetBaseMaterials[assetId];
        delete this._assetBaseMaterialsByMaster[assetId];
        delete this._assetCurrentSignature[assetId];
        // NOTE: we intentionally do NOT delete _assetLayers[assetId] so overrides can persist.
    }

    private _invalidateAssetSignaturesIfMasterSetChanged(assetId: string, masterMeshIds: string[]): void {
        const currentSignatures = this._assetCurrentSignature[assetId];
        if (!currentSignatures) {
            return;
        }

        const signatureKeys = Object.keys(currentSignatures);
        if (signatureKeys.length !== masterMeshIds.length) {
            this._assetCurrentSignature[assetId] = {};
            return;
        }

        for (let i = 0; i < masterMeshIds.length; i++) {
            if (!currentSignatures.hasOwnProperty(masterMeshIds[i])) {
                this._assetCurrentSignature[assetId] = {};
                return;
            }
        }
    }

    private _reapplyForAsset(assetId: string): void {
        // While an asset is hidden, MeshManager's hide machinery owns its
        // material — applying the override winner here would visually un-hide
        // it (e.g. a batched flush landing after a visibility sweep). The
        // unhide path re-runs the winner via maintainAsset.
        if (this._meshManager.isAssetHidden(assetId)) {
            this._invalidateAssetSignatures(assetId);
            return;
        }

        // Ask MeshManager what master meshes currently contain this asset.
        const masterMeshIds = this._meshManager.getMasterKeysOfObject(assetId);
        if (!masterMeshIds || masterMeshIds.length === 0) {
            return;
        }


        const baseMaterials = this._getStoredBaseMaterials(assetId, masterMeshIds);

        // No base => nothing to apply to.
        if (!baseMaterials || baseMaterials.length === 0) {
            return;
        }

        const perAssetLayers = this._assetLayers[assetId];

        let winningSource: string | null = null;
        let winningPriority = Infinity; // lower wins
        let winningMaterials: string[] | null = null;

        let xraySource: IMaterialOverrideLayer | undefined;
        let selectionLayer: IMaterialOverrideLayer | undefined;

        // 1) Consider GLOBAL layers
        for (const src in this._globalLayers) {
            const layer = this._globalLayers[src];
            if (!layer) continue;

            if (layer.source === "Xray")
                xraySource = layer;

            if (layer.priority < winningPriority) {
                winningPriority = layer.priority;
                winningSource = layer.source;
                winningMaterials = layer.materials;
            }
        }

        // 2) Consider PER-ASSET layers
        if (perAssetLayers) {
            for (const src in perAssetLayers) {
                const layer = perAssetLayers[src];
                if (!layer) continue;

                // Resolve selection after the ordinary appearance layers. It
                // normally wins by priority, except when the winning appearance
                // is hidden; selected hidden assets must remain invisible.
                if (layer.source === SELECTION_LAYER_SOURCE) {
                    selectionLayer = layer;
                    continue;
                }

                if (layer.priority < winningPriority) {
                    winningPriority = layer.priority;
                    winningSource = layer.source;
                    winningMaterials = layer.materials;
                }
            }
        }

        // Hack: xray should override global only if the global material is null
        if (winningMaterials != null && winningMaterials.length > 0 && winningMaterials[0] == null && xraySource) {
            winningPriority = xraySource.priority;
            winningSource = xraySource.source;
            winningMaterials = xraySource.materials;
        }

        const appearanceIsHidden = winningMaterials?.some(
            material => material === HIDDEN_MATERIAL_NAME
        ) ?? false;

        if (selectionLayer && !appearanceIsHidden && selectionLayer.priority < winningPriority) {
            winningPriority = selectionLayer.priority;
            winningSource = selectionLayer.source;
            winningMaterials = selectionLayer.materials;
        }

        const targetMaterials = winningMaterials ?? baseMaterials;
        const targetSource = winningSource ?? null;

        if (!this._assetCurrentSignature[assetId]) {
            this._assetCurrentSignature[assetId] = {};
        }

        this._invalidateAssetSignaturesIfMasterSetChanged(assetId, masterMeshIds);

        const mm = this._meshManager;

        for (let i = 0; i < masterMeshIds.length; i++) {
            const masterMeshId = masterMeshIds[i];
            if (!masterMeshId) continue;

            const matName = this._resolveMaterialForIndex(targetMaterials, baseMaterials, i);
            if (!matName) continue;

            const desiredSignature = this._makeSignature(targetSource, matName);
            const currentSignature = this._assetCurrentSignature[assetId][masterMeshId] ?? null;

            if (currentSignature === desiredSignature) {
                continue;
            }


            if (this._batchDepth > 0 || this._isBatchFlushing) {
                this._enqueueMeshMaterialChange(masterMeshId, matName, assetId);
            } else {
                mm.changeGraphicObjectsMaterial(masterMeshId, [assetId], matName, false);
            }

            this._assetCurrentSignature[assetId][masterMeshId] = desiredSignature;
        }
    }
}

export default MaterialOverrideManager;
